import React, { forwardRef } from 'react';

export interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
  size?: 'sm' | 'md' | 'lg';
  resize?: 'none' | 'vertical' | 'horizontal' | 'both';
  showCount?: boolean;
  maxLength?: number;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(({
  label,
  error,
  helperText,
  size = 'md',
  resize = 'vertical',
  showCount = false,
  maxLength,
  className = '',
  value,
  ...props
}, ref) => {
  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'px-3 py-2 text-sm';
      case 'lg':
        return 'px-4 py-3 text-base';
      default:
        return 'px-4 py-2.5 text-sm';
    }
  };

  const getStateClasses = () => {
    if (error) {
      return 'shadow-[0_0_15px_rgba(239,68,68,0.1)] focus:ring-red-500/10 bg-red-50/40';
    }
    return 'shadow-sm hover:shadow-xl hover:-translate-y-0.5 focus:ring-blue-500/10 bg-white/60 backdrop-blur-sm';
  };

  const textAreaClasses = `
    block w-full rounded-xl bg-white
    placeholder:text-slate-400
    focus:outline-none focus:ring-2 focus:ring-offset-0
    disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed
    transition-all duration-300
    ${getSizeClasses()}
    ${getStateClasses()}
    ${resize === 'none' ? 'resize-none' : ''}
    ${resize === 'vertical' ? 'resize-y' : ''}
    ${resize === 'horizontal' ? 'resize-x' : ''}
    ${resize === 'both' ? 'resize' : ''}
    ${className}
  `.trim();

  const currentLength = String(value || '').length;
  const isNearLimit = maxLength && currentLength > maxLength * 0.9;
  const isOverLimit = maxLength && currentLength > maxLength;

  return (
    <div className="space-y-1.5">
      {label && (
        <label className="block text-sm font-medium text-slate-700">
          {label}
        </label>
      )}

      <textarea
        ref={ref}
        className={textAreaClasses}
        maxLength={maxLength}
        value={value}
        {...props}
      />

      <div className="flex items-center justify-between">
        <div>
          {error && (
            <div className="flex items-center gap-1.5 text-sm text-red-600">
              <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {helperText && !error && (
            <p className="text-sm text-slate-500">
              {helperText}
            </p>
          )}
        </div>

        {(showCount || maxLength) && (
          <div className={`
            text-xs
            ${isOverLimit ? 'text-red-600' : isNearLimit ? 'text-yellow-600' : 'text-slate-400'}
          `}>
            {currentLength}{maxLength ? `/${maxLength}` : ''}
          </div>
        )}
      </div>
    </div>
  );
});

TextArea.displayName = 'TextArea';
